import cn from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { formatDateToYYYYMMDD } from '@/shared/lib';
import { Button, Checkbox, Input, Select } from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import {
  analyticsModelModel,
  AnalyticsModelSummary,
  sortAnalyticsModelsForDisplay,
  VersionStatus,
} from '@/entities/analyticsModel';
import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';

import { buildAnalyticsUrl } from '@/pages/CompanySettings/lib/buildAnalyticsUrl';

import { analyticsModelMessages } from '../../config/i18n';

import styles from './AnalyticsModelList.module.scss';

type StatusFilter = 'all' | 'active' | 'draft';

type AnalyticsModelListProps = {
  className?: string;
};

const matchesSearch = (
  model: AnalyticsModelSummary,
  query: string,
): boolean => {
  if (!query) {
    return true;
  }
  const haystack = [model.name, model.code].join(' ').toLowerCase();
  return haystack.includes(query.toLowerCase());
};

const matchesStatus = (
  model: AnalyticsModelSummary,
  filter: StatusFilter,
): boolean => {
  if (filter === 'all') {
    return true;
  }
  return model.status === (filter as VersionStatus);
};

export const AnalyticsModelList: FC<AnalyticsModelListProps> = (props) => {
  const { className } = props;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showDeleted, setShowDeleted] = useState(false);

  const allModels = useAppSelector(
    analyticsModelModel.selectors.selectAllAnalyticsModels,
  );
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const linkedSchemasMap = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasByAnalyticsModelSeriesId,
  );

  useEffect(() => {
    if (organizationId) {
      dispatch(
        analyticsModelModel.fetchAnalyticsModels({
          organization_id: organizationId,
        }),
      );
      dispatch(
        templatesModel.fetchEncounterSchemas({
          organization_id: organizationId,
          include_draft: true,
        }),
      );
    }
  }, [dispatch, organizationId]);

  const statusOptions = useMemo(
    () => [
      {
        label: formatMessage(analyticsModelMessages.filterAll),
        value: 'all',
      },
      {
        label: formatMessage(analyticsModelMessages.filterCurrent),
        value: 'active',
      },
      {
        label: formatMessage(analyticsModelMessages.filterDraft),
        value: 'draft',
      },
    ],
    [formatMessage],
  );

  const sortedModels = useMemo(
    () =>
      sortAnalyticsModelsForDisplay(allModels, {
        organizationId: organizationId ?? null,
      }),
    [allModels, organizationId],
  );

  const visibleModels = useMemo(
    () =>
      sortedModels.filter((model) => {
        if (!showDeleted && model.status === 'deleted') {
          return false;
        }
        return (
          matchesStatus(model, statusFilter) &&
          matchesSearch(model, searchQuery)
        );
      }),
    [sortedModels, showDeleted, statusFilter, searchQuery],
  );

  const handleStatusChange = (
    value: React.ChangeEvent<Element> | ValueType,
  ) => {
    if (typeof value === 'string') {
      setStatusFilter(value as StatusFilter);
    }
  };

  const handleCreateNew = () => {
    navigate(buildAnalyticsUrl({ mode: 'new' }));
  };

  const handleSelect = (model: AnalyticsModelSummary) => {
    navigate(buildAnalyticsUrl({ mode: 'edit', id: model.id }));
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-section="analytics-models"
    >
      <div className={styles.header}>
        <h1 className={cn(styles.title, 'h4')}>
          {formatMessage(analyticsModelMessages.title)}
        </h1>
        <Button
          variant="primary"
          size="medium"
          icon="plus"
          onClick={handleCreateNew}
          className={styles.createButton}
        >
          {formatMessage(analyticsModelMessages.createModel)}
        </Button>
      </div>

      <div className={styles.controlsBar}>
        <div className={styles.controls}>
          <Input
            name="analyticsModelListSearch"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={formatMessage(
              analyticsModelMessages.searchPlaceholder,
            )}
            className={styles.searchInput}
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={handleStatusChange}
            className={styles.controlSelect}
          />
          <label className={styles.toggleRow}>
            <Checkbox
              checked={showDeleted}
              onChange={() => setShowDeleted(!showDeleted)}
            />
            <span className={styles.toggleLabel}>
              {formatMessage(analyticsModelMessages.showDeleted)}
            </span>
          </label>
        </div>
        <span className={styles.itemCount}>
          {formatMessage(analyticsModelMessages.itemsCount, {
            visible: visibleModels.length,
            total: sortedModels.length,
          })}
        </span>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.colName} />
            <col className={styles.colSpecialties} />
            <col className={styles.colCreated} />
          </colgroup>
          <thead className={styles.tableHeader}>
            <tr>
              <th
                className={cn(
                  styles.tableHeaderColumn,
                  styles.tableHeaderPrimary,
                  'p2',
                )}
              >
                {formatMessage(analyticsModelMessages.name)}
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(analyticsModelMessages.linkedSpecialties)}
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(analyticsModelMessages.modelCreated)}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleModels.length === 0 ? (
              <tr>
                <td colSpan={3} className={cn(styles.emptyCell, 'p2')}>
                  {searchQuery || statusFilter !== 'all'
                    ? formatMessage(analyticsModelMessages.emptySearchResults)
                    : formatMessage(analyticsModelMessages.noModels)}
                </td>
              </tr>
            ) : (
              visibleModels.map((model) => {
                const linkedSchemas = model.series_id
                  ? linkedSchemasMap.get(model.series_id) ?? []
                  : [];
                const linkedNames = linkedSchemas.map((schema) => schema.name);

                return (
                  <tr
                    key={model.id}
                    className={cn(styles.tableRow, 'p2')}
                    onClick={() => handleSelect(model)}
                  >
                    <td className={cn(styles.tableCell, styles.nameCell)}>
                      <span className={cn(styles.cellText, styles.nameLink)}>
                        {model.name}
                      </span>
                    </td>
                    <td className={styles.tableCell}>
                      {linkedNames.length > 0 ? (
                        <div className={styles.specialtiesList}>
                          {linkedNames.map((name, index) => {
                            const key = `${name}-${index}`;

                            return (
                              <span key={key} className={styles.specialtyTag}>
                                {name}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={styles.emptySpecialties}>
                          {formatMessage(
                            analyticsModelMessages.noLinkedSpecialties,
                          )}
                        </span>
                      )}
                    </td>
                    <td className={cn(styles.tableCell, styles.metaCell)}>
                      {formatDateToYYYYMMDD(new Date(model.created_at))}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
